import { MealWithIntakes, PlanCell } from "../../data/plan";
import { IntakeWithEditedFlags } from "../../data/plan";
import { NutriValues, emptyNutriValue } from "../../data/nutriValues";
import { FoodUnit } from "../../data/foodUnit";
import { PurchaseDto } from "../../api";
import { MealIngredient } from "../../data/mealWithIngredients";
import { formatPurchaseDescription } from "../../utils/purchaseUtils";
import { isDeleted } from "../../utils/editUtils";
import {
  findById,
  isNullIndex,
  nutriValueSumReducer,
  nutriValuesFromIntake,
} from "../../utils/planUtils";

export interface ConsumedRow {
  key: string;
  name: string;
  status?: string;
  showProductPicker: boolean;
  units: FoodUnit[];
  currentUnit: FoodUnit | undefined;
  amount: number;
}

export function round1(v: number): number {
  return Math.round(v * 10) / 10;
}

export function cloneCell<T extends PlanCell>(cell: T): T {
  return JSON.parse(JSON.stringify(cell));
}

export function intakePurchaseUnits(
  intake: { purchaseUnitPurchaseId: number },
  purchases: PurchaseDto[] | undefined,
): FoodUnit[] {
  const purchase = findById(purchases ?? [], intake.purchaseUnitPurchaseId);
  return (purchase?.purchaseUnits ?? []).map((pu) => ({
    id: pu.id,
    description: pu.description,
    netWeight: pu.netWeight,
    isDefault: pu.isDefault,
  }));
}

export function consumedNutri(
  cell: MealWithIntakes,
  purchases: PurchaseDto[] | undefined,
): NutriValues {
  const active = (cell.intakes ?? []).filter(
    (i) => !isDeleted(i) && (i.amount || 0) > 0,
  );
  if (active.length === 0 || !purchases) return { ...emptyNutriValue };
  return active.reduce(
    (acc, i) => nutriValueSumReducer(acc, nutriValuesFromIntake(i, purchases)),
    { ...emptyNutriValue },
  );
}

export type IntakeStepInfo = { stepIndex: number; stepText?: string | null };

/**
 * Liefert die Schritt-Info je Intake. Der Intake trägt `stepIndex`/`stepText`
 * inzwischen selbst (beim Erzeugen aus der Rezept-Zutat), sodass keine
 * mehrdeutige Zuordnung über die Food mehr nötig ist. Angezeigt wird sie –
 * analog zur Planen-/Verzehr-Karte – nur, wenn dieselbe Food im Rezept in
 * mehreren Schritten vorkommt.
 */
export function mapIntakeStepInfos(
  mealIngredients: MealIngredient[],
  purchases: PurchaseDto[],
  intakes: IntakeWithEditedFlags[],
): (IntakeStepInfo | undefined)[] {
  const foodIdCounts = new Map<number, number>();
  mealIngredients.forEach((mi) => {
    if (isNullIndex(mi.foodUnitFoodId)) return;
    const foodId = mi.foodUnitFoodId!;
    foodIdCounts.set(foodId, (foodIdCounts.get(foodId) ?? 0) + 1);
  });

  return intakes.map((intake) => {
    if (intake.stepIndex == null) return undefined;
    const foodId = purchases.find(
      (p) => p.id === intake.purchaseUnitPurchaseId,
    )?.foodId;
    if (foodId == null) return undefined;
    if ((foodIdCounts.get(foodId) ?? 0) <= 1) return undefined;
    return { stepIndex: intake.stepIndex, stepText: intake.stepText };
  });
}

export function buildConsumedRows(
  cell: MealWithIntakes,
  purchases: PurchaseDto[] | undefined,
): ConsumedRow[] {
  const intakes = cell.intakes ?? [];
  const rows: ConsumedRow[] = [];
  intakes.forEach((intake: IntakeWithEditedFlags, idx) => {
    if (isDeleted(intake)) return;
    const purchase = findById(purchases ?? [], intake.purchaseUnitPurchaseId);
    const units = intakePurchaseUnits(intake, purchases);
    const foodId = purchase?.foodId;
    const alternativeCount = (purchases ?? []).filter(
      (p) => p.foodId === foodId,
    ).length;
    const name =
      formatPurchaseDescription(purchase) ||
      intake.purchaseUnitPurchaseDescription;
    rows.push({
      key: String(idx),
      name,
      status: intake.edited ? name : undefined,
      showProductPicker: alternativeCount > 1,
      units,
      currentUnit: units.find((u) => u.id === intake.purchaseUnitId),
      amount: intake.amount,
    });
  });
  return rows;
}
